import { AggregationType } from "~/ui-rtk/constants/charts"

// Current + comparison date ranges for the KPI page's secondary
// `compareDateRange` cube queries (brand value breakdown, campaign media,
// ad-set initiative breakouts).
//
// Invariant: every window returned here must be identical to the window the
// main `revenue_kpi_table` query builds for the same granularity (see
// `MetricsMapping.revenue_kpi_table.q` in constants/metrics-mapping.ts). The
// column headers are generated from the main dataset's rows only, and each
// secondary dataset joins its rows to those columns by bucket start date
// (processMetrics with columnJoin), so a bucket outside the main window is
// never shown. Equal fetch windows are what keeps every column populated.
//
// This invariant was broken once, at daily granularity: this helper fetched a
// trailing 364-day window while the main query fetched 60 days ending
// yesterday. `week_1` of the secondary datasets was then ten months older than
// `week_1` of the main dataset, so every Brand Value breakdown cell rendered a
// bucket from roughly ten months before its column header, and the
// data-availability warnings (correctly reading each row's true bucket date)
// flagged every visible cell as predating Amazon revenue history. The
// kpi-compare-date-range spec pins this helper to the main query per
// granularity so the windows cannot silently drift apart again.
//
// Granularity semantics (mirroring the main query):
// - Daily: last 60 days ending yesterday; comparison is the SAME 60 calendar
//   days one year earlier. The YoY merge pairs daily buckets by month+day
//   (getYoyComparisonKey), so the comparison window must be a calendar-year
//   shift — a fixed 364/365-day offset would misalign every month+day key and
//   no bucket would ever find its prior-year counterpart.
// - Weekly: trailing 52 weeks ending now; comparison is the 52 weeks before.
// - Monthly: trailing 12 months aligned to month boundaries; the comparison
//   range extends through the last day of the prior-year current month so the
//   partial current month compares against that full prior-year month.
/** How the comparison window pairs with the current one (see buildKpiWindows). */
export type KpiCompareMode = "yoy" | "window"

export interface KpiWindowOptions {
  /**
   * Explicit current window [start, end] (inclusive; read as LOCAL calendar
   * dates). Omitted → the dashboard's fixed trailing window per granularity.
   */
  range?: [Date, Date]
  /**
   * 'yoy' (default): the dashboard's year-back construction applied to the
   * current window. An explicit [start, end] pair is used verbatim as the
   * comparison window ('window' compare mode — offset-from-start pairing).
   */
  compare?: "yoy" | [Date, Date]
}

export interface KpiWindows {
  /** [start, end] of the current window, local-serialized 'YYYY-MM-DD'. */
  currentRange: [string, string]
  /** [start, end] of the comparison window, local-serialized 'YYYY-MM-DD'. */
  comparisonRange: [string, string]
  /**
   * [start, end] the day-granularity availability query fetches: the union
   * span of both windows (default windows: [comparisonStart, currentEnd],
   * since the YoY comparison always precedes the current window).
   */
  availabilityRange: [string, string]
  /**
   * 'yoy' → the merge pairs buckets by calendar identity (getYoyComparisonKey);
   * 'window' → by offset from each window's start (getWindowComparisonKey),
   * and custom/typed-metric comparisons shift by the same window offset.
   */
  compareMode: KpiCompareMode
}

// Serialize with LOCAL date components (mirrors the main query). toISOString
// converts to UTC first, which shifts the calendar day for viewers off UTC.
const toDateStr = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`

/**
 * The single window builder behind every KPI data-pipeline query (main
 * revenue_kpi_table, brand value, media campaigns, ad-set initiatives, and the
 * daily availability fetch — all five must share one output, see the
 * window invariant above).
 *
 * With no options this reproduces the dashboard's fixed trailing windows
 * BYTE-IDENTICALLY (the legacy construction below is kept verbatim, and the
 * kpi-compare-date-range spec pins it to the main query). An explicit `range`
 * fetches that window instead; its 'yoy' comparison applies the same
 * per-granularity year-back construction to the custom range (daily/monthly =
 * calendar-year shift with the monthly full-prior-month tail, weekly = 364
 * days). An explicit `compare` range is fetched verbatim.
 */
export function buildKpiWindows(
  granularity: AggregationType,
  opts?: KpiWindowOptions,
): KpiWindows {
  const compare = opts?.compare ?? "yoy"
  const explicitRange = opts?.range

  let currentStart: Date
  let currentEnd: Date
  let comparisonStart: Date
  let comparisonEnd: Date

  if (explicitRange) {
    currentStart = explicitRange[0]
    currentEnd = explicitRange[1]
    if (granularity === AggregationType.DAILY) {
      // Calendar-year shift — the YoY merge pairs daily buckets by month+day.
      comparisonStart = new Date(currentStart)
      comparisonStart.setFullYear(comparisonStart.getFullYear() - 1)
      comparisonEnd = new Date(currentEnd)
      comparisonEnd.setFullYear(comparisonEnd.getFullYear() - 1)
    } else if (granularity === AggregationType.MONTHLY) {
      // Month-aligned year-back of the range start, extended through the full
      // prior-year month of the range end (the default window's partial-month
      // tail rule, generalized to any end date).
      comparisonStart = new Date(
        currentStart.getFullYear() - 1,
        currentStart.getMonth(),
        1,
      )
      comparisonEnd = new Date(
        currentEnd.getFullYear() - 1,
        currentEnd.getMonth() + 1,
        0,
      )
    } else {
      // Weekly: exactly 364 days back (52·7 — lands on the same weekday, the
      // only shift the 364-day week pairing can match).
      comparisonStart = new Date(currentStart)
      comparisonStart.setDate(comparisonStart.getDate() - 364)
      comparisonEnd = new Date(currentEnd)
      comparisonEnd.setDate(comparisonEnd.getDate() - 364)
    }
  } else {
    // DEFAULT dashboard windows — kept verbatim (byte-identical guarantee).
    const now = new Date()

    if (granularity === AggregationType.DAILY) {
      currentEnd = new Date(now)
      currentEnd.setDate(currentEnd.getDate() - 1)

      currentStart = new Date(currentEnd)
      currentStart.setDate(currentStart.getDate() - 59)

      comparisonStart = new Date(currentStart)
      comparisonStart.setFullYear(comparisonStart.getFullYear() - 1)
      comparisonEnd = new Date(currentEnd)
      comparisonEnd.setFullYear(comparisonEnd.getFullYear() - 1)
    } else if (granularity === AggregationType.MONTHLY) {
      currentEnd = now
      currentStart = new Date(now.getFullYear(), now.getMonth() - 12, 1)

      comparisonStart = new Date(now.getFullYear() - 2, now.getMonth(), 1)
      comparisonEnd = new Date(now.getFullYear() - 1, now.getMonth() + 1, 0)
    } else {
      currentEnd = now
      currentStart = new Date(now)
      currentStart.setDate(currentStart.getDate() - 52 * 7)

      comparisonStart = new Date(currentStart)
      comparisonStart.setDate(comparisonStart.getDate() - 52 * 7)
      comparisonEnd = new Date(currentStart)
    }
  }

  // An explicit compare window overrides whichever comparison was derived.
  if (Array.isArray(compare)) {
    comparisonStart = compare[0]
    comparisonEnd = compare[1]
  }

  const currentRange: [string, string] = [
    toDateStr(currentStart),
    toDateStr(currentEnd),
  ]
  const comparisonRange: [string, string] = [
    toDateStr(comparisonStart),
    toDateStr(comparisonEnd),
  ]
  // Union span, min/max via date-only string comparison (lexicographic ===
  // chronological): an explicit compare window is not guaranteed to precede
  // the current one.
  const availabilityRange: [string, string] = [
    comparisonRange[0] < currentRange[0] ? comparisonRange[0] : currentRange[0],
    comparisonRange[1] > currentRange[1] ? comparisonRange[1] : currentRange[1],
  ]

  return {
    currentRange,
    comparisonRange,
    availabilityRange,
    compareMode: Array.isArray(compare) ? "window" : "yoy",
  }
}

/**
 * Legacy default-window accessor — a thin delegate over buildKpiWindows with
 * no options, kept for its four consumers' shape and for the spec that pins
 * these windows to the main query (see the module comment above).
 */
export function getKpiCompareDateRange(granularity: AggregationType) {
  const { currentRange, comparisonRange } = buildKpiWindows(granularity)
  return {
    currentStartStr: currentRange[0],
    currentEndStr: currentRange[1],
    comparisonStartStr: comparisonRange[0],
    comparisonEndStr: comparisonRange[1],
  }
}

const MS_PER_DAY = 86_400_000
const utcDayNumber = (dateOnly: string) =>
  Date.parse(`${dateOnly}T00:00:00Z`) / MS_PER_DAY

/**
 * How many merged weekly buckets a weekly current window keeps (queryTheCube
 * trims each merged weekly dataset to its most recent N buckets, uniformly
 * across all four datasets; the siblings join to the columns by bucket date,
 * and a uniform trim keeps their windows aligned when the cubes are equally
 * fresh). Derived from
 * the window's inclusive day span: the default 365-day trailing window always
 * yields 53 cube buckets with a leading partial → keeps 52 (byte-identical to
 * the old fixed slice(-52)); an N-week explicit range keeps N, dropping only
 * a leading partial bucket. Never below 1 — a sub-week range still has one
 * (partial) bucket.
 */
export function weeklyBucketCount(currentRange: [string, string]): number {
  const inclusiveDays =
    utcDayNumber(currentRange[1]) - utcDayNumber(currentRange[0]) + 1
  return Math.max(1, Math.floor(inclusiveDays / 7))
}
